import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { AlertCircle } from "lucide-react";
import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Route, Routes } from "react-router";
import { Toaster } from "@hubertai/hubert-component-library";
import ProtectedRoute from "./ProtectedRoute";
import { PageRoute } from "./constants";
import VerifyEmail from "./helpers/EmailVerification";
import GoogleCallback from "./helpers/google/GoogleCallback";
import CenteredLayout from "./layouts/CenteredLayout";
import OutreachLayout from "./pages/outreachCandidate/components/OutreachLayout";
import ErrorPage from "./pages/ErrorPage";
import Impersonation from "./pages/impersonation/Impersonation";
import {
    AdviceBeforeInterviewArticle,
    Article,
    Dashboard,
    ForgotPassword,
    HelpCenter,
    Interview,
    InterviewCompletedPageInviteEmail,
    LearnWithHubert,
    Login,
    LoginInviteEmail,
    LoginInviteEmailInterviewCompleted,
    PreInterviewPage,
    Registration,
    UnsubscribeFromEmails,
    YourInterviews,
    YourProfile,
} from "./pages/index";
import PostInstantApply from "./pages/instantJobApply/PostInstantApply";
import PreInstantApply from "./pages/instantJobApply/PreInstantApply";
import ProcessInvitation from "./pages/invitation/ProcessInvitation";
import RealTimeScreeningLogin from "./pages/login/RealTimeScreening/Login";
import RealTimeScreeningPostInterviewLogin from "./pages/login/RealTimeScreening/PostInterviewLogin";
import RealTimeScreeningPostInterviewRegister from "./pages/login/RealTimeScreening/PostInterviewRegister";
import OutreachChatPage from "./pages/outreachCandidate/OutreachChatPage";
import OutreachCandidatePage from "./pages/outreachCandidate/OutreachCandidatePage";
import TransitionPage from "./pages/transition/TransitionPage";
import ScrollToTop from "./utils/ScrollToTop";
import { getCookie } from "./utils/cookieHandlers";
import { useHydrateUserSession } from "./utils/customHooks";
import { useLanguageDetection } from "./utils/customHooks/useLanguageDetection";
const App = () => {
    const { i18n, t } = useTranslation();
    const { detectLanguage } = useLanguageDetection();
    useHydrateUserSession();
    useEffect(() => {
        const languageCookie = getCookie("language");
        if (languageCookie) {
            i18n.changeLanguage(languageCookie);
        } else if (navigator.languages) {
            const supportedLocale = detectLanguage();
            i18n.changeLanguage(supportedLocale);
        }
    }, [i18n, detectLanguage]);

    useEffect(() => {
        document.documentElement.lang = t("locale");
        document.documentElement.dir = i18n.dir();
    }, [i18n, i18n.language]);
    const queryClient = new QueryClient();
    return (
        <>
            <QueryClientProvider client={queryClient}>
                <Toaster />
                <ScrollToTop>
                    <Routes>
                        <Route element={<CenteredLayout />}>
                            <Route path={PageRoute.ROOT} element={<Login />} />
                            <Route path={PageRoute.REGISTRATION} element={<Registration />} />
                            <Route
                                path={PageRoute.POST_INTERVIEW_REGISTRATION}
                                element={<InterviewCompletedPageInviteEmail />}
                            />
                            <Route path={PageRoute.PRE_INTERVIEW_LOGIN} element={<LoginInviteEmail />} />
                            <Route
                                path={PageRoute.POST_INTERVIEW_LOGIN}
                                element={<LoginInviteEmailInterviewCompleted />}
                            />
                            <Route path={PageRoute.PRE_INTERVIEW} element={<PreInterviewPage />} />
                            <Route path={PageRoute.GOOGLE_CALLBACK} element={<GoogleCallback />} />
                            <Route path={PageRoute.EMAIL_VERIFICATION_CALLBACK} element={<VerifyEmail />} />
                            <Route path={PageRoute.INVITATION_VERIFICATION} element={<ProcessInvitation />} />
                            <Route path={PageRoute.INSTANT_APPLY.INDEX}>
                                <Route path={PageRoute.INSTANT_APPLY.POST_INTERVIEW} element={<PostInstantApply />} />
                                <Route path={PageRoute.INSTANT_APPLY.PRE_INTERVIEW} element={<PreInstantApply />} />
                                <Route path={PageRoute.INSTANT_APPLY.LOGIN} element={<RealTimeScreeningLogin />} />
                                <Route
                                    path={PageRoute.INSTANT_APPLY.POST_INTERVIEW_SIGNIN}
                                    element={<RealTimeScreeningPostInterviewLogin />}
                                />
                                <Route
                                    path={PageRoute.INSTANT_APPLY.POST_INTERVIEW_SIGNUP}
                                    element={<RealTimeScreeningPostInterviewRegister />}
                                />
                            </Route>
                            <Route path={PageRoute.PASSWORD_RESET} element={<ForgotPassword />} />
                            <Route
                                path={`${PageRoute.INTERVIEW_ADVICE}/:lang`}
                                element={<AdviceBeforeInterviewArticle />}
                            />
                            <Route path={PageRoute.TRANSITION_PAGE} element={<TransitionPage />} />
                            <Route path="" />
                            <Route element={<ProtectedRoute />}>
                                <Route path={PageRoute.DASHBOARD} element={<Dashboard />} />
                                <Route path={PageRoute.INTERVIEWS}>
                                    <Route index element={<YourInterviews />} />
                                    <Route path=":id" element={<Interview />} />
                                </Route>
                                <Route path={PageRoute.PROFILE} element={<YourProfile />} />
                                <Route path={PageRoute.LEARN_WITH_HUBERT} element={<LearnWithHubert />} />
                                <Route path={PageRoute.HELP_CENTER} element={<HelpCenter />} />
                                <Route path={`${PageRoute.LEARN_WITH_HUBERT_ARTICLE}/:id`} element={<Article />} />R
                            </Route>
                            <Route path={PageRoute.UNSUBSCRIBE_EMAIL} element={<UnsubscribeFromEmails />} />
                            <Route path={PageRoute.IMPERSONATION} element={<Impersonation />} />
                            <Route
                                path="*"
                                element={
                                    <ErrorPage
                                        Icon={AlertCircle}
                                        title="Are you sure you were looking for this ?"
                                        description="The page you are looking for does not exist."
                                        hideNextSteps={false}
                                    />
                                }
                            />
                        </Route>
                        <Route element={<OutreachLayout />}>
                            <Route path={PageRoute.OUTREACH_CANDIDATE} element={<OutreachCandidatePage />} />
                            <Route path={PageRoute.OUTREACH_CANDIDATE_CHAT} element={<OutreachChatPage />} />
                        </Route>
                    </Routes>
                </ScrollToTop>
            </QueryClientProvider>
        </>
    );
};

export default App;
